import React from 'react';
import { useTranslation } from 'react-i18next';

const BLUE_ACCENT = '#3B7BF4';
const TEXT_DARK = '#1A1A2E';
const BORDER_LIGHT = '#D1D5DB';

// Each language shown in its own script (עברית, English, Français, Español)
export const SUPPORTED_LANGUAGES = [
  { code: 'he', nativeLabel: 'עברית', flag: '🇮🇱' },
  { code: 'en', nativeLabel: 'English', flag: '🇺🇸' },
  { code: 'fr', nativeLabel: 'Français', flag: '🇫🇷' },
  { code: 'es', nativeLabel: 'Español', flag: '🇪🇸' },
  { code: 'it', nativeLabel: 'Italiano', flag: '🇮🇹' },
] as const;

const I18N_RETURN_PATH_KEY = 'i18n_return_path';

interface LanguageSelectorProps {
  /** Path to redirect to after reload (e.g. page user came from) */
  fromPath?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ fromPath }) => {
  const { i18n } = useTranslation();

  const handleSelect = async (code: string) => {
    // Persist immediately so refresh and reload both use the new language
    localStorage.setItem('i18nextLng', code);
    if (fromPath) {
      sessionStorage.setItem(I18N_RETURN_PATH_KEY, fromPath);
    }
    await i18n.changeLanguage(code);
    document.documentElement.lang = code;
    document.documentElement.dir = code === 'he' ? 'rtl' : 'ltr';
    // Reload so the whole app re-initializes with the new language (guarantees UI updates + persistence)
    window.location.reload();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {SUPPORTED_LANGUAGES.map(({ code, nativeLabel, flag }) => (
        <button
          key={code}
          type="button"
          onClick={() => handleSelect(code)}
          style={{
            width: '100%',
            padding: '16px 20px',
            background: i18n.language?.startsWith(code)
              ? 'rgba(59, 123, 244, 0.12)'
              : 'transparent',
            border: `2px solid ${i18n.language?.startsWith(code) ? BLUE_ACCENT : BORDER_LIGHT}`,
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 600,
            color: TEXT_DARK,
            cursor: 'pointer',
            textAlign: 'inherit',
            transition: 'background 0.2s ease, border-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!i18n.language?.startsWith(code)) {
              e.currentTarget.style.background = 'rgba(59, 123, 244, 0.06)';
              e.currentTarget.style.borderColor = BLUE_ACCENT;
            }
          }}
          onMouseLeave={(e) => {
            if (!i18n.language?.startsWith(code)) {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = BORDER_LIGHT;
            }
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '16px',
            }}
          >
            <div>{flag}</div>
            <div>{nativeLabel}</div>
          </div>
        </button>
      ))}
    </div>
  );
};

export default LanguageSelector;
